// ParksContext — globaalne shared state kõikide parkide andmete jaoks.
//
// Üks polling loop kogu rakendusele (App.jsx wrap'ib Provider'isse). Kui
// kasutaja vahetab lehte (Töölaud ↔ Seadmed), andmed säilivad mälus, ühtegi
// uut päringut ei käivitata. Backend pollib taustal iga 5s ning hoiab oma
// cache värskena — frontend ainult loeb sealt ja võtab WebSocket push'e
// kohe vastu kui live update tuleb.
//
// Andmevoog:
//   1. Provider mount → 1 päring /api/parks (täita esmakuva)
//   2. setInterval iga 5s → uus /api/parks (fallback kui WS pole)
//   3. WebSocket /ws kuulab `{type: 'parks', data}` → uuendab kohe
//
// Komponendid kasutavad: const { data, loading, ... } = useParks();

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getViewTenant } from './AuthContext';

const POLL_INTERVAL = 5000; // 5s
const STALE_THRESHOLD = 20000; // 20s — kui andmed pole uuenenud, näita "Offline"

const ParksContext = createContext(null);

export function ParksProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(0);
  const [, setTick] = useState(0);

  const inFlight = useRef(false);
  const mounted = useRef(true);
  const wsRef = useRef(null);

  // HTTP fallback poll (kui WS ei tööta või enne kui esimene WS sõnum tuleb)
  const refresh = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await fetch('/api/parks', { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!mounted.current) return;
      setData(json);
      setError(null);
      setLastSuccess(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e.message);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  // WebSocket kuulab live update (backend saadab iga 5s `{type: 'parks', data}`)
  const connectWS = () => {
    try {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Superadmini kliendi-vaade: backend saadab siis ainult selle tenanti pargid
      const vt = getViewTenant();
      const url = `${proto}//${window.location.host}/ws${vt ? `?tenant=${encodeURIComponent(vt)}` : ''}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'parks' && msg.data) {
            if (!mounted.current) return;
            setData(msg.data);
            setError(null);
            setLastSuccess(Date.now());
            setLoading(false);
          }
        } catch (_) {
          // ignoreeri parse vead
        }
      };

      ws.onclose = () => {
        // Reconnect 3s pärast
        if (mounted.current) {
          setTimeout(connectWS, 3000);
        }
      };

      ws.onerror = () => {
        // Sulgemine triggerib reconnect'i
      };
    } catch (_) {
      // Reconnect 3s pärast
      if (mounted.current) setTimeout(connectWS, 3000);
    }
  };

  useEffect(() => {
    mounted.current = true;

    // Esmane HTTP päring → kohe näha andmed
    refresh();

    // WS kuulamine — push uuendused
    connectWS();

    // HTTP fallback poll — kui WS ei tööta või lihtsalt kindluse jaoks
    const interval = setInterval(refresh, POLL_INTERVAL);

    // Stale-check ticker — sunnib re-rendri iga 4s, et `isStale` värske
    const tickInterval = setInterval(() => setTick((t) => t + 1), 4000);

    return () => {
      mounted.current = false;
      clearInterval(interval);
      clearInterval(tickInterval);
      try { wsRef.current?.close(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStale = lastSuccess > 0 && Date.now() - lastSuccess > STALE_THRESHOLD;

  const value = {
    data,
    loading,
    error,
    refresh,
    lastSuccess,
    isStale,
  };

  return <ParksContext.Provider value={value}>{children}</ParksContext.Provider>;
}

export function useParks() {
  const ctx = useContext(ParksContext);
  if (!ctx) {
    throw new Error('useParks() peab olema ParksProvider sees');
  }
  return ctx;
}

export default ParksContext;
